import React from "react";
import {
  CloseIcon,
  ModelIcon,
  PlusIcon,
  SearchIcon,
} from "../../Common/SvgIcons/WorkflowIcons";

const getTimeAgo = (dateStr) => {
  if (!dateStr) return "";
  const created = new Date(String(dateStr).replace(" ", "T"));
  if (Number.isNaN(created.getTime())) return "";
  const diffMinutes = Math.floor((Date.now() - created.getTime()) / 60000);
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes} min${diffMinutes === 1 ? "" : "s"} ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
};

export default function ModelArchitectureSidebar({
  cards = [],
  selectedArchitecture,
  searchTerm,
  onSearchTermChange,
  showSearch,
  onShowSearch,
  onCloseSearch,
  onImport,
  onSelect,
}) {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredCards = cards.filter((item) => {
    if (!normalizedSearch) return true;
    return [
      item.key,
      item.name,
      item.architecture,
      item.framework,
      item.taskType,
      item.benchmarkDataset,
      item.domain,
    ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
  });

  return (
    <>
      <div className="container-card datasets-pannel-height left-pannel d-none d-md-block p-3 deploy-model-sidebar">
      <div className="d-flex flex-column gap-3" style={{ justifyContent: "space-between" }}>
        <div className="training-experiment-history-header">
          {!showSearch ? (
            <span className="training-experiment-history-title">
              <ModelIcon
                className="training-experiment-history-icon"
                style={{ width: "20px", height: "17px" }}
              />
              <span>MODEL ARCHITECTURES</span>
            </span>
          ) : (
            <div className="training-experiment-search-bar">
              <SearchIcon className="training-experiment-search-bar__icon" />
              <input
                autoFocus
                type="text"
                className="training-experiment-search-bar__input"
                placeholder="Search architectures"
                value={searchTerm}
                onChange={(event) => onSearchTermChange(event.target.value)}
              />
              <div className="training-experiment-search-bar__divider" />
              <button
                type="button"
                className="plain-button2 text-danger"
                onClick={onCloseSearch}
                aria-label="Close search"
              >
                <CloseIcon className="wf-icon text-secondary-small" />
              </button>
            </div>
          )}

          <div className="training-experiment-history-actions">
            {!showSearch && (
              <button
                type="button"
                className="plain-button2"
                onClick={onShowSearch}
                title="Search models"
                aria-label="Search models"
              >
                <SearchIcon className="training-experiment-search-toggle" />
              </button>
            )}
            <span className="training-experiment-count-badge">
              {normalizedSearch ? filteredCards.length : cards.length}
            </span>
          </div>
        </div>

        <button type="button" className="training-sidebar-create bg-main" onClick={onImport}>
          <span>Import Model</span>
        </button>
      </div>

      <div className="d-flex flex-column datasets-view">
        <div className="d-flex" style={{ width: "100%" }}>
          <div className="scrollbar data-select-cards gap-2 d-flex pt-0 pt-md-2">
            {filteredCards.length === 0 && normalizedSearch && (
              <div className="w-100 text-center text-secondary py-4 px-2">
                No architectures match “{searchTerm.trim()}”.
              </div>
            )}

            {filteredCards.map((item) => {
              const updatedAgo = getTimeAgo(item.updatedOn);
              const latestLabel = item.latestVersion
                ? String(item.latestVersion).startsWith("v")
                  ? item.latestVersion
                  : `v${item.latestVersion}`
                : null;
              const metaBits = [item.framework, item.taskType].filter(Boolean);
              const detailBits = [
                latestLabel,
                item.versionCount > 1 ? `${item.versionCount} versions` : null,
                item.classCount > 0 ? `${item.classCount} classes` : null,
                updatedAgo,
              ].filter(Boolean);

              return (
                <button
                  key={item.key}
                  type="button"
                  className={`datases-card-container model-repo-card ${
                    selectedArchitecture === item.key ? "data-select-active" : ""
                  }`}
                  onClick={() => onSelect(item)}
                >
                  <div className="model-repo-card-row">
                    <div className="model-repo-card-info">
                      <h6
                        className={`second-title ${
                          selectedArchitecture === item.key ? "text-primary" : ""
                        } mb-0`}
                      >
                        {item.name.replace(/_/g, " ") || "Model Name"}
                      </h6>
                      {metaBits.length > 0 && (
                        <p className="text-secondary font-small mt-1 mb-0">
                          {metaBits.join(" · ")}
                        </p>
                      )}
                      {detailBits.length > 0 && (
                        <p className="model-repo-card-details mb-0">
                          {detailBits.join(" · ")}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      </div>

      <div className="d-flex gap-2 d-md-none overflow-auto p-1 px-2 container-card dataset-mobile-strip deploy-architecture-strip">
        <button
          type="button"
          className="deploy-architecture-strip__add bg-main"
          onClick={onImport}
          aria-label="Import model"
          title="Import model"
        >
          <PlusIcon className="wf-icon text-white" />
        </button>

        <div className="scrollbar data-select-cards gap-2 d-flex">
          {cards.map((item) => {
            const latestLabel = item.latestVersion
              ? String(item.latestVersion).startsWith("v")
                ? item.latestVersion
                : `v${item.latestVersion}`
              : null;
            const mobileMeta = [
              item.framework,
              latestLabel,
              item.versionCount > 0
                ? `${item.versionCount} version${item.versionCount === 1 ? "" : "s"}`
                : null,
            ].filter(Boolean);

            return (
              <button
                key={item.key}
                type="button"
                className={`mobile-dataset-card mobile-model-architecture-card ${
                  selectedArchitecture === item.key ? "data-select-active" : ""
                }`}
                onClick={() => onSelect(item)}
                title={item.name || item.architecture}
              >
                <span className="mobile-model-architecture-card__icon" aria-hidden="true">
                  <ModelIcon className="wf-icon-small" />
                </span>
                <span className="mobile-model-architecture-card__copy">
                  <span
                    className={`mobile-model-architecture-card__title ${
                      selectedArchitecture === item.key ? "text-primary" : ""
                    }`}
                  >
                    {(item.name || item.architecture || "Model").replace(/_/g, " ")}
                  </span>
                  {mobileMeta.length > 0 && (
                    <span className="mobile-model-architecture-card__meta">
                      {mobileMeta.join(" · ")}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
